import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import {
  AuthService,
  EmployeeService,
  HolidaysService,
  ObservablesService,
  ProjectService,
} from 'src/app/shared/services';
import {
  CalendarModel,
  TimeSlots,
  EmployeeProject,
  IHoliday,
  IProject,
  LoggedDay,
} from 'src/app/shared/models';
import { EmploymentType } from 'src/app/shared/enums';
import {
  CALENDAR_LEAVE_TYPES,
  CalendarLeaveType,
  toCalendarLeaveType,
} from 'src/app/shared/constants';
import {
  DaysLoggedTime,
  ICalendarV2Request,
  ICalendarV2RequestLoggedTime,
  Request,
} from 'src/app/shared/models/request-calendar.model';
import { CalendarHeaderComponent } from './calendar-header/calendar-header.component';
import { PersonalCalendarService } from 'src/app/shared/services/personal-calendar.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import {
  CalendarHelper,
  DayDetails,
} from 'src/app/shared/services/helpers/calendar-helper.service';
import { FormControl } from '@angular/forms';
import { TimeLogController } from 'src/app/shared/services/time-log.controller';
import { MatDialog } from '@angular/material/dialog';
import { TimeLogTableDialogComponent } from 'src/app/shared/dialogs/time-log-table-dialog/time-log-table-dialog.component';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: false,
})
export class CalendarComponent implements OnDestroy, AfterViewInit {
  projects = input<EmployeeProject[]>([]);
  private authService = inject(AuthService);
  private calendarService = inject(PersonalCalendarService);
  private holidayService = inject(HolidaysService);
  private changeDetection = inject(ChangeDetectorRef);
  private projectService = inject(ProjectService);
  private observablesService = inject(ObservablesService);
  private calendarHelper = inject(CalendarHelper);
  private employeeService = inject(EmployeeService);
  private ctrl = inject(TimeLogController);
  dialog = inject(MatDialog);

  readonly projectId = input<number>(undefined);
  readonly isCalendarHome = input(true);
  readonly calendarHeader = viewChild<CalendarHeaderComponent>('calendarHeader');
  readonly paginator = viewChild<MatPaginator>('paginator');
  readonly projectAutocomplete = viewChild<MatAutocomplete>('auto');

  public calendar: CalendarModel[] = [];
  public allHolidays: Array<IHoliday>;
  public isLocalLoading: boolean = true;
  public project: IProject;
  public filter: string = '';
  public pageSize = 20;
  public currentPage = 0;
  public currentPageData: CalendarModel[];
  public pageSizeOptions = [20, 40, 60, 80];
  public daysDetails: DayDetails[];
  public projectsControl: FormControl = new FormControl();
  public projectList = signal<EmployeeProject[]>([]);
  public leaveTypes = CALENDAR_LEAVE_TYPES;
  public selectedLeaveTypes: CalendarLeaveType[] = [];

  private projectsValue = toSignal(this.projectsControl.valueChanges);
  public projectFilterText = computed(() => {
    const value = this.projectsValue();

    return typeof value === 'string' ? value : '';
  });

  private destroy$ = new Subject<void>();

  ngAfterViewInit(): void {
    this.loadData();
    this.observablesService.updateCalendar
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadData());
    this.changeDetection.detectChanges();

    const savedPageSize = parseInt(localStorage.getItem('pageSize'));
    if (savedPageSize) {
      this.pageSize = +savedPageSize;
    }
  }

  private loadData(): void {
    this.updateDays();
    if (this.isCalendarHome()) {
      this.getCurrentMonthRequests();
    } else {
      this.initTimeLoggingPage();
    }

    const projectId = this.projectId();
    if (projectId) {
      this.projectService
        .getProjectByIdClientUnauthorized(projectId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => (this.project = data));
    }
  }

  private calculateLogTimes(
    loggedHours: number = 0,
    hasMorningVacation: boolean,
    hasAfternoonVacation: boolean,
  ): { morning: number; afternoon: number } {
    if (!loggedHours) {
      return { morning: 0, afternoon: 0 };
    }

    if (hasMorningVacation && hasAfternoonVacation) {
      return { morning: 0, afternoon: 0 };
    }

    if (hasMorningVacation) {
      return { morning: 0, afternoon: loggedHours };
    }

    if (hasAfternoonVacation) {
      return { morning: loggedHours, afternoon: 0 };
    }

    if (loggedHours >= 4) {
      return { morning: 4, afternoon: loggedHours - 4 };
    }

    return { morning: loggedHours, afternoon: 0 };
  }

  private normalizeDays(employee: ICalendarV2Request | ICalendarV2RequestLoggedTime) {
    const requests = this.mapRequestsToDays(employee.requests);
    const loggedTimes =
      'loggedTimesPerProjectPerEmployee' in employee
        ? this.mapLoggedTimeToDays(employee.loggedTimesPerProjectPerEmployee)
        : {};

    const [firstMonth] = this.calendarHeader().getMonthDates();

    const startOfMonth = moment(firstMonth).startOf('month');
    const endOfMonth = moment(firstMonth).endOf('month');

    const days: Record<string, TimeSlots> = {};

    const currentDay = startOfMonth.clone();
    while (currentDay.isSameOrBefore(endOfMonth, 'day')) {
      const date = currentDay.format('YYYY-MM-DD');

      days[date] = {};

      const { morning, afternoon } = this.calculateLogTimes(
        loggedTimes[date]?.hours,
        !!requests[date]?.morning?.vacationType,
        !!requests[date]?.afternoon?.vacationType,
      );

      days[date].morning = {
        vacationType: null,
        subType: null,
        leaveType: null,
        icon: false,
        ...requests[date]?.morning,
        loggedTime: morning,
      };

      days[date].afternoon = {
        vacationType: null,
        subType: null,
        leaveType: null,
        icon: false,
        ...requests[date]?.afternoon,
        loggedTime: afternoon,
      };

      currentDay.add(1, 'day');
    }

    return days;
  }

  private mapRequestsToDays = (requests?: Request[]) => {
    if (!requests?.length) return {};

    const days: Record<string, TimeSlots> = {};

    for (const request of requests) {
      const requestDate = moment(request.fromDate);
      const leaveType = toCalendarLeaveType(request.vacationType, request.subType);

      let icon = true;

      for (let index = 0; index < request.pattern.length; index++) {
        const pattern = request.pattern[index];

        if (pattern === '0') continue;

        const currentDate = requestDate.clone().add(index, 'days').format('YYYY-MM-DD');

        days[currentDate] ??= {};

        if (['1', '3'].includes(pattern)) {
          days[currentDate].morning = {
            icon,
            vacationType: request.vacationType,
            subType: request.subType,
            leaveType,
          };
        }

        if (['2', '3'].includes(pattern)) {
          days[currentDate].afternoon = {
            icon,
            vacationType: request.vacationType,
            subType: request.subType,
            leaveType,
          };
        }

        icon = false;
      }
    }

    return days;
  };

  private mapLoggedTimeToDays = (daysLoggedTime?: DaysLoggedTime[]) => {
    if (!daysLoggedTime?.length) return {};

    return daysLoggedTime.reduce<Record<string, LoggedDay>>((days, day) => {
      const currentDate = moment(day.date).format('YYYY-MM-DD');

      days[currentDate] = {
        ...day,
        hours: days[currentDate] ? days[currentDate].hours + day.hours : day.hours,
      };

      return days;
    }, {});
  };

  private updateDays(): void {
    const [firstMonthDate, lastMonthDate] = this.calendarHeader().getMonthDates();
    this.calendarHeader().currentMonthTitle = firstMonthDate.format('MMMM YYYY');

    const holidays = this.holidayService.getAllHolidaysByYear(firstMonthDate.year());
    holidays.pipe(takeUntil(this.destroy$)).subscribe((responseHolidays) => {
      this.allHolidays = responseHolidays;
      this.daysDetails = this.calendarHelper.getMonthDays(lastMonthDate);
    });
  }

  private updatePageData() {
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, this.calendar.length);

    this.currentPageData = this.calendar.slice(startIndex, endIndex);
  }

  private async initTimeLoggingPage(): Promise<void> {
    let projects: EmployeeProject[];
    if (this.projects().length) {
      projects = this.projects();
    } else {
      projects = await this.employeeService.loadMyProjects(this.authService.getUser().id);
    }

    const sortedProjects = [...projects].sort((a, b) =>
      a.name.toLocaleUpperCase() > b.name.toLocaleUpperCase() ? 1 : -1,
    );
    this.projectList.set(sortedProjects);

    const [firstProject] = sortedProjects;

    if (!firstProject) {
      this.handleLoading(false);
      return;
    }

    this.projectsControl.setValue(firstProject);
    await this.ctrl.changeProject(firstProject.id);
    this.getCurrentMonthTimeLogging(firstProject.id);
  }

  getCurrentMonthRequests = () => {
    const [firstMonthDate] = this.calendarHeader().getMonthDates();

    this.calendarService
      .getAllRequestsInCheckedCalendars(firstMonthDate.format('YYYY-MM'), this.authService.userId())
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.calendar = data.map((emp) => ({
          id: emp.employeeId,
          fullName: emp.fullName,
          employmentType: emp.employmentType,
          email: emp.fullName,
          workDays: emp.workDays,
          days: this.normalizeDays(emp),
          totalMonthWorkingHours: emp.totalMonthWorkingHours,
          totalWorkingHours: emp.totalWorkingHours,
        }));
        this.updateDays();
        this.handleLoading(false);

        if (this.isCalendarHome()) {
          this.applyFilter(this.filter);
        } else if (this.projectsControl.value) {
          this.getCurrentMonthTimeLogging(this.projectsControl.value.id);
        }
      });
  };

  getCurrentMonthTimeLogging = (projectId: number) => {
    const [firstMonthDate, lastDateCurrentMonth] = this.calendarHeader().getMonthDates();
    this.calendarService
      .getTimeLogging(
        projectId,
        firstMonthDate.format('YYYY-MM-DD'),
        lastDateCurrentMonth.format('YYYY-MM-DD'),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.calendar = data.map((emp) => ({
          id: emp.employeeId,
          fullName: emp.fullName,
          employmentType: emp.employmentType,
          email: emp.fullName,
          workDays: emp.workDays,
          days: this.normalizeDays(emp),
          totalMonthWorkingHours: emp.totalMonthWorkingHours,
          totalWorkingHours: emp.totalWorkingHours,
        }));

        this.updateDays();
        this.applyFilter(this.filter);
        this.handleLoading(false);
      });
  };

  resetCalendar = () => {
    this.paginator().firstPage();
    this.applyFilter('');
    this.getCurrentMonthRequests();
  };

  onPageChange(event) {
    this.currentPage = event.pageIndex;
    this.updatePageData();
  }

  onPageSizeChange(event: MatSelectChange): void {
    this.pageSize = event.value;
    localStorage.setItem('pageSize', this.pageSize.toString());
    this.paginator().pageSize = this.pageSize;
    this.paginator().firstPage();
    this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
  }

  applyFilter(event: string) {
    this.filter = event;
    this.updateVisibleEmployees();
  }

  onLeaveTypesChange(leaveTypes: CalendarLeaveType[]) {
    this.selectedLeaveTypes = leaveTypes;
    this.updateVisibleEmployees();
  }

  clearLeaveTypes() {
    this.onLeaveTypesChange([]);
  }

  isFiltered(): boolean {
    return !!this.filter || this.selectedLeaveTypes.length > 0;
  }

  private updateVisibleEmployees() {
    if (!this.isFiltered()) {
      this.updatePageData();
      return;
    }

    const name = (this.filter ?? '').trim().toLowerCase();

    this.currentPageData = this.calendar.filter(
      (employee) =>
        employee.fullName.toLowerCase().includes(name) && this.hasSelectedLeaveType(employee),
    );
  }

  private hasSelectedLeaveType(employee: CalendarModel): boolean {
    if (!this.selectedLeaveTypes.length) {
      return true;
    }

    return Object.values(employee.days).some((slots) =>
      [slots.morning, slots.afternoon].some(
        (slot) => !!slot?.leaveType && this.selectedLeaveTypes.includes(slot.leaveType),
      ),
    );
  }

  handleLoading = (val: boolean) => {
    this.isLocalLoading = val;
  };

  async onProjectChange(event: MatAutocompleteSelectedEvent) {
    const { id } = event.option.value;
    await this.ctrl.changeProject(id);
    this.getCurrentMonthTimeLogging(id);
  }

  expandEmployee(empId: number) {
    const [firstMonthDate, lastDateCurrentMonth] = this.calendarHeader().getMonthDates();

    this.ctrl
      .handleSelect(empId, this.projectsControl.value.id, firstMonthDate, lastDateCurrentMonth)
      .then(() => {
        this.dialog.open(TimeLogTableDialogComponent, {
          data: this.ctrl.data.value,
        });
      });
  }

  isHolidayForEmployee(day: DayDetails, employmentType: EmploymentType) {
    return employmentType === EmploymentType.BULGARIAN_EMPLOYEE
      ? this.allHolidays.some((holiday) => holiday.date === day.fullDateString)
      : false;
  }

  displayFn(project: EmployeeProject): string {
    return project ? `${project.name}` : null;
  }

  markSelectedProjectOption(): void {
    const selected = this.projectsControl.value;

    if (!selected) {
      return;
    }

    this.projectAutocomplete()
      ?.options?.find((option) => option.value?.id === selected.id)
      ?.select(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
